/** @format */

import { create } from "zustand";
import type { Notification } from "@/api/notificationsApi";

export interface ToastItem {
  id: string;
  notification: Notification;
  createdAt: number;
}

const MAX_TOASTS = 5;

const DEMO_NOTIFICATIONS: Pick<Notification, "category" | "title" | "body" | "data">[] =
  [
    {
      category: "race_start",
      title: "Green Flag!",
      body: "The Daytona 500 is underway!",
      data: {},
    },
    {
      category: "race_incident",
      title: "Caution Flag",
      body: "Caution out on lap 47 for debris in turn 3.",
      data: { incidentType: "caution" },
    },
    {
      category: "lead_change",
      title: "Lead Change",
      body: "Kyle Larson takes the lead from Chase Elliott on lap 112.",
      data: {},
    },
    {
      category: "favorite_driver_pit",
      title: "Pit Stop",
      body: "Denny Hamlin is heading to pit road — 4 tires and fuel.",
      data: {},
    },
    {
      category: "race_fastest_lap",
      title: "Fastest Lap",
      body: "Martin Truex Jr. sets fastest lap: 29.412s",
      data: {},
    },
    {
      category: "favorite_driver_incident",
      title: "Incident Alert",
      body: "Tyler Reddick involved in contact on the backstretch.",
      data: {},
    },
    {
      category: "race_end",
      title: "Checkered Flag!",
      body: "Kyle Larson wins the Daytona 500!",
      data: {},
    },
  ];

let demoIndex = 0;

interface NotificationToastState {
  toasts: ToastItem[];
  overflowCount: number;
  addToast: (notification: Notification) => void;
  removeToast: (id: string) => void;
  addDemoToast: () => void;
}

export const useNotificationToastStore = create<NotificationToastState>(set => ({
  toasts: [],
  overflowCount: 0,
  addToast: notification => {
    const item: ToastItem = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      notification,
      createdAt: Date.now(),
    };
    set(state => {
      const combined = [item, ...state.toasts];
      const overflow =
        combined.length > MAX_TOASTS
          ? state.overflowCount + (combined.length - MAX_TOASTS)
          : state.overflowCount;
      return {
        toasts: combined.slice(0, MAX_TOASTS),
        overflowCount: overflow,
      };
    });
  },
  removeToast: id => {
    set(state => {
      const remaining = state.toasts.filter(t => t.id !== id);
      return {
        toasts: remaining,
        overflowCount: remaining.length === 0 ? 0 : state.overflowCount,
      };
    });
  },
  addDemoToast: () => {
    const demo = DEMO_NOTIFICATIONS[demoIndex % DEMO_NOTIFICATIONS.length];
    demoIndex++;
    const notification: Notification = {
      id: `demo-${Date.now()}`,
      category: demo.category,
      title: demo.title,
      body: demo.body,
      data: demo.data,
      status: "sent",
      deliveryMethod: "websocket",
      createdAt: new Date().toISOString(),
      sentAt: new Date().toISOString(),
      readAt: null,
    };
    useNotificationToastStore.getState().addToast(notification);
  },
}));
